import React, { FC, useEffect, useState, useCallback, useRef, useMemo } from "react";
import styles from "./styles.module.less";
import { Empty, Spin, Modal, Alert } from "antd";
import { useTranslation, Trans } from "react-i18next";
import classnames from "classnames";

import { useMediaSize } from "@/hooks/useMediaSize";
import {
  SortableElement,
  SortableContainer,
  arrayMove,
  SortableContainerProps,
} from "react-sortable-hoc";
import { PCAndPadAppItemCard } from "../PCAndPadAppItemCard";
import { AppInfo } from "../types";
import { useTitle } from "@/hooks/useTitle";
import { PCAndPadAddApp } from "../PCAndPadAddApp";
import { detectBrowser, BrowserInfo } from "@/utils/detectBrowser";
import { isBrowserSupportAsa } from "@/utils";

// import HtmlWebpackPlugin from "html-webpack-plugin";

const APP_ITEM_WIDTH = 180; // 同 ../variable.less 中的 @app-item-size
const APP_ITEM_MARGIN = 12; // 同 ../variable.less 中的 @app-item-margin

const PLUGIN_CLOSE_LOCAL_STORAGE_KEY = "asa_plugin_alert_closed";

const useContainerWidth = () => {
  const { isPhoneMedia } = useMediaSize();

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState("100%");

  const computedContainerWidth = useCallback(
    () =>
      requestAnimationFrame(() => {
        if (!containerRef.current) {
          return;
        }

        if (isPhoneMedia) {
          setContainerWidth("100%");
          return;
        }

        const containerWidth = containerRef.current.clientWidth;

        // 我也看不懂
        const count = parseInt(
          String(
            (containerWidth + APP_ITEM_MARGIN * 2) /
            (APP_ITEM_WIDTH + APP_ITEM_MARGIN * 2)
          )
        );

        setContainerWidth(
          count * APP_ITEM_WIDTH + APP_ITEM_MARGIN * (count * 2) + "px"
        );
      }),
    [isPhoneMedia]
  );

  useEffect(() => {
    computedContainerWidth();

    window.addEventListener("resize", computedContainerWidth);

    return () => window.removeEventListener("resize", computedContainerWidth);
  }, [computedContainerWidth]);

  return {
    containerRef,
    containerWidth,
  };
};

const useAsaAlert = (orderedApps: AppInfo[]) => {
  const [supportAsa, setSupportAsa] = useState(false);

  useEffect(() => {
    const browserInfo = detectBrowser();

    if (browserInfo instanceof BrowserInfo) {
      setSupportAsa(isBrowserSupportAsa(browserInfo));
    }
  }, []);

  const showInstallPluginAlert = useMemo(() => {
    return (
      orderedApps.some((item) => item.isAsa) &&
      supportAsa &&
      !document.getElementById("_authing_asa_plugin_info_") &&
      !Number(localStorage.getItem(PLUGIN_CLOSE_LOCAL_STORAGE_KEY))
    );
  }, [orderedApps, supportAsa]);


  return showInstallPluginAlert
}

export const PCAndPadAppList: FC<{
  allApps: AppInfo[];
  loadingAllApp: boolean;
  orderedApps: AppInfo[];
  setOrderedApps: React.Dispatch<React.SetStateAction<AppInfo[]>>;
  loadingOrderedApp: boolean;
  fetchOrderedApps: () => Promise<void>;
  removeAppRequest: (id: string) => Promise<void>;
  installAppRequest: (id: string) => Promise<void>;
  sortApps: (ids: string[]) => Promise<void>;
}> = ({
  allApps,
  loadingAllApp,
  loadingOrderedApp,
  orderedApps,
  fetchOrderedApps,
  setOrderedApps,
  removeAppRequest,
  installAppRequest,
  sortApps,
}) => {
    const { t } = useTranslation();
    useTitle(`${window.__config__.name} ${t("common.appPanel")}`, "");

    const { containerRef, containerWidth } = useContainerWidth();

    const [removingApp, setRemovingApp] = useState(false);
    const [addingApp, setAddingApp] = useState(false);
    const [sortingApp, setSortingApp] = useState(false);

    const [addVisible, setAddVisible] = useState(false);

    const showAsaAlert = useAsaAlert(orderedApps)

    const removeApp = useCallback(
      async (v: any) => {
        if (v.id.includes("/")) {
          // 子应用删除
          return Modal.confirm({
            title: t("common.deleteApp"),
            width: 600,
            content: (
              <Trans
                i18nKey="user.launchpadDeleteApp"
                values={{
                  mainAppName: v.mainAppName,
                  subAppName: v.name,
                }}
              >
                删除<b> {v.name} </b>
                应用的同时会删除其他所有
              <b> {v.mainAppName} </b>
                应用，你确定要删除此应用吗？注：只要你有访问权限，你可以随时通过按钮「添加应用」把它们添回到你的应用面板。
            </Trans>
            ),
            async onOk() {
              setRemovingApp(true);
              try {
                await removeAppRequest(v.id.split("/")[0]);
              } finally {
                setRemovingApp(false);
              }
            },
          });
        }

        removeAppRequest(v.id);
      },
      [removeAppRequest, t]
    );

    const installApp = useCallback(
      async (addId: string) => {
        setAddingApp(true);
        try {
          await installAppRequest(addId);
        } finally {
          fetchOrderedApps();
          setAddingApp(false);
        }
      },
      [fetchOrderedApps, installAppRequest]
    );

    const SortableItem = SortableElement(({ appInfo }: { appInfo: AppInfo }) => (
      <PCAndPadAppItemCard onRemoveApp={removeApp} appInfo={appInfo} />
    ));
    const SortableAppContainer: React.ComponentClass<SortableContainerProps, FC> =
      SortableContainer(({ children }: any) => (
        <div className={styles.sortContainer}>{children}</div>
      ));

    const AddButton = () => {
      return (
        <div
          className={classnames(styles.addBtn)}
          onClick={() => setAddVisible(true)}
        >
          <div className={classnames(styles.addAppIcon)}>+</div>
          <p className={styles.addAppText}>{t("common.addApp")}</p>
        </div>
      );
    };

    return (
      <>
        <div className={styles.container}>

          {showAsaAlert && (
            <Alert
              style={{
                borderRadius: 4,
                marginBottom: 24,
              }}
              message={
                <Trans i18nKey="user.asa.downloadPlugin">
                  为了快捷登录，点击此处下载
                  <a
                    // eslint-disable-next-line react/jsx-no-target-blank
                    target="_blank"
                    href={`${window.__config__?.docs?.host}/guides/asa/#%E4%B8%8B%E8%BD%BD%E6%8F%92%E4%BB%B6`}
                  >
                    Authing 浏览器插件。
                  </a>
                </Trans>
              }
              type="warning"
              closable
              onClose={() =>
                localStorage.setItem(PLUGIN_CLOSE_LOCAL_STORAGE_KEY, "1")
              }
            />
          )}


          <Spin
            spinning={loadingOrderedApp || removingApp || addingApp || sortingApp}
          >
            <div ref={containerRef} className={styles.containerWrap}>
              <div
                style={{
                  width: containerWidth,
                }}
                className={styles.appList}
              >
                {!loadingAllApp && allApps?.length <= 0 && (
                  <div className={styles.emptyApps}>
                    <Empty description={t("common.noOpenedApp")} />
                  </div>
                )}

                <SortableAppContainer
                  onSortEnd={async ({ oldIndex, newIndex }: any) => {
                    if (oldIndex === newIndex) {
                      return;
                    }

                    const nextOrders = arrayMove(orderedApps, oldIndex, newIndex);
                    setOrderedApps(nextOrders);

                    setSortingApp(true);
                    try {
                      await sortApps(nextOrders.map((item) => item.id));
                    } catch (e) {
                      console.error(e);
                    } finally {
                      setSortingApp(false);
                    }
                  }}
                  axis="xy"
                  shouldCancelStart={(evt) => {
                    // 触摸时不允许拖拽
                    // if (evt instanceof TouchEvent) {
                    //   return true;
                    // }

                    // 禁止拖拽的元素
                    let target = evt.target as HTMLElement | null;
                    while (target) {
                      if (target.dataset.disableDrag) {
                        return true;
                      }
                      target = target.parentElement;
                    }

                    return false;
                  }}
                  // 不起作用，是我不会用？
                  // helperClass={styles.fuck}
                  // pressDelay={isPhoneMedia ? 1000 : 0}
                  helperContainer={document.body}
                >
                  {!loadingAllApp && <AddButton />}
                  {orderedApps?.map((v, index) => {
                    return (
                      <SortableItem
                        key={`item-${v.id}`}
                        index={index}
                        appInfo={v}
                      />
                    );
                  })}
                </SortableAppContainer>
              </div>
            </div>
          </Spin>
        </div>

        <PCAndPadAddApp
          visible={addVisible}
          onCancel={() => setAddVisible(false)}
          allApps={allApps}
          orderedApps={orderedApps}
          removeApp={removeApp}
          installApp={installApp}
        />
      </>
    );
  };
